// import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useEffect, useState } from "react";
import styles from "./chart.module.css";
import { TQuestionData } from "../../../services/types/user.types";
import PieChartComp from "../pieChart/pieChart";
import BarChartComponent from "../barchart/barchart";
import Select from "react-select";
import { useAppSelector } from "../../../services/hooks";
import { diagramsTypesSelector } from "../../../services/selectors/utils.selectors";
import { useLazyGetDiagramsTypesQuery } from "../../../services/api/utils.api";
import { useUpdateDiagramsTypeMutation } from "../../../services/api/dashboards.api";
import Tooltip from "../../tooltip/tooltip";
import { GroupStatusTable } from "../groupStatusTable/groupStatusTable";
import Loader from "../../loader/loader";

interface ChartProps {
  data: TQuestionData;
  total: number;
  color?: string;
  setDataToAnalyze: (data: TQuestionData[]) => void;
  isPopup?: boolean;
  onDiagramTypeChange?: () => void;
}

const componentMap: Record<number, React.ComponentType<any>> = {
  1: PieChartComp,
  2: BarChartComponent,
};
const Chart: React.FC<ChartProps> = ({
  data,
  total,
  color,
  setDataToAnalyze,
  isPopup,
  onDiagramTypeChange,
}) => {
  const Component = componentMap[data.diagramsTypesId];

  const diagramsTypes = useAppSelector(diagramsTypesSelector);

  const [getDiagramsTypes] = useLazyGetDiagramsTypesQuery();
  const [updateDiagramsType, {isLoading: diagramLoading}] = useUpdateDiagramsTypeMutation();

  const [isGroup, setIsGroup] = useState<boolean>(false);
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);

  useEffect(() => {
    if (!diagramsTypes || !diagramsTypes.length) {
      getDiagramsTypes();
    }
  }, [diagramsTypes, getDiagramsTypes]);

  useEffect(() => {
    const isDuplicateOption = data.options.some(
      (item, idx, arr) =>
        arr.findIndex((x) => x.desc === item.desc) !== idx &&
        item.group?.groupId
    );

    setIsGroup(isDuplicateOption || data.isEmotion);
  }, [data]);

  const handleChartChange = (selectedOption: number) => {
    updateDiagramsType({
      companyId: 1,
      DiagramsTypeId: selectedOption,
      QuestionsPrefenceId: data.questionsId,
    }).then((res) => {
      if ("data" in res) {
        // Call the refresh callback to reload data with current filters
        onDiagramTypeChange?.();
      }
    });
  };

  if (!Component) return <h1>No diagram selected</h1>;

  return (
    <div className={`${styles.barChart} ${isPopup ? styles.popupBarChart : ""}`}>
      <div
        className={styles.chart_title_container}
        style={{
          backgroundColor: color,
          position: "relative",
        }}
      >
        <p
          className={styles.chart_title}
          onMouseEnter={() => setShowTitleTooltip(true)}
          onMouseLeave={() => setShowTitleTooltip(false)}
        >
          {data.desc}
        </p>
        <p className={styles.chart_total}>{data.totalResponds}</p>
        <Tooltip isShown={showTitleTooltip}>{data.desc}</Tooltip>
      </div>
      {!isGroup ? (
        <>
          <div
            className={styles.chartSelect}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Select
              options={diagramsTypes}
              defaultValue={diagramsTypes.find(
                (item) => Number(item.value) === data.diagramsTypesId
              )}
              value={diagramsTypes.find(
                (item) => Number(item.value) === data.diagramsTypesId
              )}
              onChange={(t) => t && handleChartChange(Number(t.value))}
              classNamePrefix="react-select"
              styles={customSelectStyles}
              isSearchable={false}
            />
          </div>
          {diagramLoading ? <Loader /> : <Component total={total} data={data} isPopup={isPopup} />}
        </>
      ) : (
        <div className={styles.GroupedGraph}>
          <GroupStatusTable question={data} totalClients={total} />
        </div>
      )}
    </div>
  );
};

export default Chart;

const customSelectStyles = {
  container: (provided: any) => ({
    ...provided,
    padding: "1px 0 0 6px",
  }),
  control: (provided: any) => ({
    ...provided,
    minHeight: "28px",
    height: "28px",
    padding: "1px 0 0 6px",
    fontSize: "12px",
    borderRadius: "6px",
    borderColor: "#d1d5db",
    boxShadow: "none",
    overflow: "visible",
    backgroundColor: "rgba(219, 240, 254, 1)",
    border: "1px solid rgba(9, 143, 239, 1)",
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: "28px",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: "2px",
    color: "rgba(9, 143, 239, 1)",
    "&:hover": {
      color: "rgba(9, 143, 239, 1)",
    },
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    padding: "2px",
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: "28px",
    padding: "0 6px",
    overflow: "visible",
  }),
  input: (provided: any) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: "12px",
    padding: "6px 8px",
    backgroundColor: state.isFocused ? "rgba(200, 230, 250, 1)" : "transparent",
    color: "rgba(9, 143, 239, 1)",
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "rgb(247, 248, 248)",
    border: "1px solid rgba(9, 143, 239, 1)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    padding: "0",
    fontSize: "12px",
    borderRadius: "6px",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "rgba(9, 143, 239, 1)",
    fontWeight: "bold",
    fontSize: "12px",
    direction: "rtl",
  }),
};
